import { getBuilding } from '../recursive-element.helpers';
import { IBuilding, Project } from '@shared/models/project.model';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { getAllPoints } from './shape.helpers';
import { Point2D } from '@shared/models/geometry.model';
import { moveCoordinate } from '../geometry/coordinates.helpers';
import { getCenter, movePoints } from '../geometry/geometry.helpers';
import { mutateDesign } from './mutators/design.mutator';
import { guards } from '../typeguards.helpers';
import { v4 } from 'uuid';
import { translateBlueprintUnderlay } from './blueprint-underlay.helpers';
import { ensureDesignHasBuilding } from './factories/design.factory';

export {
  applyScaleBuildingToGfa,
  createSquareDesignFromGfa,
  isEmptyBuildingDesign,
  scaleBuildingToGfa,
} from './scale-building-gfa.helpers';
export type {
  ScaleBuildingToGfaResult,
  ScaleBuildingToGfaStatus,
} from './scale-building-gfa.helpers';

/**
 * Footprint midpoint closer than this is already the local origin.
 * A later pass otherwise nudges underlay centers by a float ulp, and the
 * persist check treats that as a failed save (DEV-3835).
 */
const NORMALIZED_ORIGIN_EPSILON_METERS = 1e-6;

/**
 * Get the building design from a project or building.
 * @param root
 * @returns
 */
export const getBuildingDesignFromProjectOrBuilding = (
  root: Project | IBuilding,
): IBuilding['design'] => getBuilding(root).design;

export const normalizePoints = (points: Point2D[]): Point2D[] => {
  const boundingBoxCenter = getCenter(...points);
  const offset: Point2D = [-boundingBoxCenter[0], -boundingBoxCenter[1]];
  return movePoints(points, offset);
};

/**
 * Rebase every building so the location pin sits at the combined footprint
 * midpoint. All shapes are treated as one outline: their shared bounding-box
 * center moves to (0, 0) and {@link BuildingDesign.location} shifts by the
 * same amount so the buildings stay put on the map.
 */
export const normalizeDesignLocationAndPoints = (
  design: BuildingDesign,
): BuildingDesign => {
  const nextDesign = ensureDesignHasBuilding(design);

  const allPoints = getAllPoints(nextDesign);
  const boundingBoxCenter = allPoints.length ? getCenter(...allPoints) : [0, 0];

  // Exact zero is rare after the first recenter; sub-millimetre leftovers are noise.
  if (
    Math.abs(boundingBoxCenter[0]) <= NORMALIZED_ORIGIN_EPSILON_METERS &&
    Math.abs(boundingBoxCenter[1]) <= NORMALIZED_ORIGIN_EPSILON_METERS
  ) {
    return nextDesign;
  }

  const offset: Point2D = [-boundingBoxCenter[0], -boundingBoxCenter[1]];
  const normalizedDesign = mutateDesign.storey.all.points.translate(
    nextDesign,
    offset,
  );

  const locationOffset: Point2D = [boundingBoxCenter[0], boundingBoxCenter[1]];
  const afterX = moveCoordinate(
    normalizedDesign.location,
    [Math.abs(locationOffset[0]), 'meters'],
    [locationOffset[0] >= 0 ? 90 : 270, 'degrees'],
  );
  const adjustedLocation = moveCoordinate(
    afterX,
    [Math.abs(locationOffset[1]), 'meters'],
    [locationOffset[1] >= 0 ? 180 : 0, 'degrees'],
  );

  const blueprintUnderlays = normalizedDesign.blueprintUnderlays?.map(
    (underlay) => translateBlueprintUnderlay(underlay, offset),
  );

  return {
    ...normalizedDesign,
    location: adjustedLocation,
    ...(blueprintUnderlays?.length ? { blueprintUnderlays } : {}),
  };
};

/**
 * Regenerates all IDs in a design with new UUIDs while keeping references intact.
 * Empty {@link BuildingDesign.buildings} is healed to one building first so a
 * copy cannot drop the required slot (DEV-2576).
 * This includes:
 * - Activity IDs
 * - Storey IDs
 * - Shape IDs within each storey
 * - ActivityReference IDs within each storey
 * - Updates activity_id references in ActivityReferences to point to new activity IDs
 * @param design - The design to regenerate IDs for
 * @returns A new design with all IDs regenerated and references updated
 */
export const regenerateIdsInDesign = <
  T extends BuildingDesign | undefined,
  R = T extends BuildingDesign ? T : undefined,
>(
  design: T,
): R => {
  if (guards.is.undefined(design)) return undefined as R;

  const source = ensureDesignHasBuilding(design);

  // Create mapping from old activity IDs to new UUIDs
  const activityIdMap = new Map<string, string>();
  const regeneratedActivities = source.activities.map((activity) => {
    const newId = v4();
    activityIdMap.set(activity.id, newId);
    return {
      ...activity,
      id: newId,
    };
  });

  // Regenerate storey IDs and update shapes and activity references
  const regenerateStorey = (
    storey: BuildingDesign['buildings'][number]['storeys'][number],
  ) => {
    // Regenerate shape IDs
    const regeneratedShapes = storey.shapes.map((shape) => ({
      ...shape,
      id: v4(),
    }));

    // Regenerate activity reference IDs and update activity_id references
    const regeneratedActivityRefs = storey.activities.map((activityRef) => {
      const newActivityId = activityIdMap.get(activityRef.activity_id);
      if (!newActivityId) {
        throw new Error(
          `Activity reference points to non-existent activity: ${activityRef.activity_id}`,
        );
      }
      return {
        ...activityRef,
        id: v4(),
        activity_id: newActivityId,
      };
    });

    return {
      ...storey,
      id: v4(),
      shapes: regeneratedShapes,
      activities: regeneratedActivityRefs,
    };
  };

  // Regenerate building ids and their storeys
  const regeneratedBuildings = source.buildings.map((building) => ({
    ...building,
    id: v4(),
    storeys: building.storeys.map(regenerateStorey),
  }));

  const regeneratedUnderlays = design.blueprintUnderlays?.map((underlay) => ({
    ...underlay,
    id: v4(),
  }));

  return {
    ...source,
    activities: regeneratedActivities,
    buildings: regeneratedBuildings,
    ...(regeneratedUnderlays?.length
      ? { blueprintUnderlays: regeneratedUnderlays }
      : {}),
  } as R;
};
